import { type UIEvent, useEffect, useRef } from 'react'
import { SpinnerLoader, Typography, type IDropdownItem } from 'shared/ui'
import { Scrollbar } from 'shared/ui/Scrollbar'
import { WidgetSearch, WidgetItem } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import styles from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/styles.module.scss'

type IBookingStep2Props = {
  items: IDropdownItem[]
  focusedIndex: number
  isLoading: boolean
  search: string
  onSearch: (value: string) => void
  onSelect: (index: number) => void
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

export const BookingStep2 = ({
  items,
  focusedIndex,
  isLoading,
  search,
  onSearch,
  onSelect,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: IBookingStep2Props) => {
  const focusedItemRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    focusedItemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [focusedIndex])

  const handleScroll = (e: UIEvent) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return
    const target = e.target as HTMLDivElement
    if (target.scrollHeight <= target.scrollTop + target.offsetHeight + 100) {
      onLoadMore()
    }
  }

  return (
    <>
      <WidgetSearch value={search} onChange={onSearch} />
      {isLoading ? (
        <div className={styles.loading}>
          <SpinnerLoader size={20} ariaLabel={'BookingWidgetStep2'} />
        </div>
      ) : !items.length ? (
        <Typography variant={'body-rg-regular'} ariaLabel='noResults' className={styles.noResults}>
          No results found
        </Typography>
      ) : (
        <Scrollbar autoHeight autoHeightMax={290} onScroll={handleScroll}>
          <div className={styles.meetingList}>
            {items.map((item, index) => {
              const isFocused = index === focusedIndex && !item.disabled

              return (
                <div key={item.id} ref={isFocused ? focusedItemRef : undefined}>
                  <WidgetItem
                    isFocused={isFocused}
                    isDisabled={item.disabled}
                    badgeIndex={index}
                    onClick={() => onSelect(index)}
                    tooltipProps={item.disabled ? item.tooltipProps : undefined}
                    rightNode={
                      item.labelRight ? (
                        <Typography
                          variant={'body-rg-regular'}
                          ariaLabel={'meetingUrl'}
                          color={item.disabled ? 'var(--content-primary-disabled)' : 'tertiary'}
                          ellipsis
                          tooltipProps={{
                            label: item.labelRight,
                          }}
                          tag={'div'}
                        >
                          {item.labelRight}
                        </Typography>
                      ) : undefined
                    }
                  >
                    <Typography
                      variant={'body-rg-regular'}
                      ariaLabel={item.label}
                      ellipsis
                      tooltipProps={{
                        label: item.label,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </WidgetItem>
                </div>
              )
            })}
            {isLoadingMore && (
              <div className={styles.loading}>
                <SpinnerLoader size={20} ariaLabel={'BookingWidgetStep2LoadMore'} />
              </div>
            )}
          </div>
        </Scrollbar>
      )}
    </>
  )
}
